// useWakeWord — hands-free activation for the portal voice assistant (ADR-0025).
//
// While `active`, it loads the openWakeWord pipeline, opens the mic, and ticks
// the detector on an interval; when the score crosses `threshold` it fires
// `onWake` (debounced). The audio never leaves the device — only the score is
// computed in-browser. Flipping `active` false tears everything down and
// RELEASES the mic, so the assistant's own STT can take over cleanly.
//
// Lifecycle is keyed on `active` only: caller sets it to
// `enabled && hudClosed && micAvailable`, so the wake word listens in the
// background and pauses (mic freed) while the HUD is open.

import React from "react";
import { WakeWordDetector, startWakeMic } from "./wakeword";

const TICK_MS = 120;
const DEFAULT_THRESHOLD = 0.5;
// Ignore re-fires within this window so one "hey jarvis" triggers once.
const REFIRE_GUARD_MS = 2500;

export function useWakeWord({
  active,
  threshold = DEFAULT_THRESHOLD,
  onWake,
}: {
  active: boolean;
  threshold?: number;
  onWake: () => void;
}): void {
  // Keep the latest callback without restarting the detector each render.
  const onWakeRef = React.useRef(onWake);
  onWakeRef.current = onWake;

  React.useEffect(() => {
    if (!active) return;
    let stopped = false;
    let det: WakeWordDetector | null = null;
    let stopMic: (() => void) | null = null;
    let interval: number | null = null;
    let busy = false;
    let lastFire = 0;

    void (async () => {
      try {
        det = new WakeWordDetector();
        await det.init();
        if (stopped) {
          det.dispose();
          return;
        }
        stopMic = await startWakeMic((pcm) => det?.push(pcm));
        interval = window.setInterval(async () => {
          if (busy || !det) return;
          busy = true;
          try {
            const s = await det.tick();
            const now =
              typeof performance !== "undefined" ? performance.now() : Date.now();
            if (s.warm && s.score >= threshold && now - lastFire > REFIRE_GUARD_MS) {
              lastFire = now;
              onWakeRef.current();
            }
          } finally {
            busy = false;
          }
        }, TICK_MS);
      } catch {
        // Mic denied / WASM or model load failed → wake word silently off.
        // The launcher orb + typed input still work.
      }
    })();

    return () => {
      stopped = true;
      if (interval !== null) window.clearInterval(interval);
      stopMic?.();
      det?.dispose();
    };
  }, [active, threshold]);
}
